/* eslint-disable parog-web/no-glint-expect-error-directive */
import type Store from '@ember-data/store';
import { concat, hash } from '@ember/helper';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import type { BufferedChangeset } from 'ember-changeset/types';
import t from 'ember-intl/helpers/t';
import add from 'ember-math-helpers/helpers/add';
import eq from 'ember-truth-helpers/helpers/eq';
import { getGroupedBioExams } from 'parog-web/components/bio-exam-results/utils/groupedBioExams';
import type { Signature as ForcProviderSignature } from 'parog-web/components/forc/provider';
import FuiDropdown from 'parog-web/components/fui-dropdown';
import type { Value as FuiDropdownValue } from 'parog-web/components/fui-dropdown';
import CommentSuggestions from 'parog-web/components/prescription/bio-exam-edition-form/comment-suggestions';
import capitalize from 'parog-web/helpers/capitalize';
import tWithDefault from 'parog-web/helpers/t-with-default';
import type BioExamOrder from 'parog-web/models/bio-exam-order';
import type ErrorsService from 'parog-web/services/errors';
import Checkbox from 'ui-kit/components/changeset/checkbox';
import Dropdown from 'ui-kit/components/changeset/dropdown';
import LabeledTextArea from 'ui-kit/components/changeset/labeled-text-area';
import fuiAccordion from 'wise-fui-modules/modifiers/fui-accordion';

interface Args {
  changeset: BufferedChangeset;
  isCreationFromTemplate: boolean;
  isActive?: boolean;
  form: ForcProviderSignature['Blocks']['content'][0];
}
export default class PrescriptionBioExamEditionFormContent extends Component<Args> {
  @service declare errors: ErrorsService;
  @service declare store: Store;

  declare prescriptibleOrders: BioExamOrder[];

  get frequencyOptions() {
    return [
      '1-day',
      '1-week',
      '2-week',
      '1-month',
      '2-month',
      '3-month',
      '4-month',
      '5-month',
      '6-month',
      '1-year',
      'none'
    ];
  }

  get orderedPrescriptibleOrders() {
    return getGroupedBioExams(this.prescriptibleOrders);
  }

  get selectedFrequency(): string | undefined {
    const fqcy = this.args.changeset.get('frequency');
    return fqcy;
  }

  get validityOptions() {
    return [
      'Un mois',
      'Deux mois',
      'Trois mois',
      'Quatre mois',
      'Cinq mois',
      'Six mois',
      'Un an'
    ];
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);

    this.prescriptibleOrders = this.store.peekAll(
      'bio-exam-order'
    ) as unknown as BioExamOrder[];
  }

  onFrequencyChanged = (selectedValue: FuiDropdownValue) => {
    this.args.changeset.set('frequency', selectedValue);
  };

  onOrderAdded = (value: string) => {
    const loincs = [...(this.args.changeset.get('exams') || [])];
    loincs.push(value);
    this.args.changeset.set('exams', loincs);
  };

  onOrderRemoved = (value: string) => {
    const loincs = [...(this.args.changeset.get('exams') as string[])];
    const indexToRemove = loincs.findIndex(loinc => loinc == value);
    loincs.splice(indexToRemove, 1);
    this.args.changeset.set('exams', loincs);
  };

  updateAldRelatedComments = (suggestion: string): void => {
    this.#appendComment('aldRelatedComments', suggestion);
  };

  updateNonAldRelatedComments = (suggestion: string): void => {
    this.#appendComment('comments', suggestion);
  };

  #appendComment(changesetKey: string, value: string): void {
    const comments = this.args.changeset.get(changesetKey) || '';
    const newComments = comments.concat(`${value}\n`);
    this.args.changeset.set(changesetKey, newComments);
  }

  <template>
    <h3 class='ui dividing header'>
      {{if
        @isCreationFromTemplate
        "Création d'une ordonnance à partir d'un modèle"
        "Création d'une nouvelle ordonnance"
      }}</h3>

    <span class='ui primary text'>Ajouter un examen à partir de notre base de
      données</span>
    <div class='field'>
      <FuiDropdown
        class='selection clearable search multiple'
        @selected={{changesetGet @changeset 'exams'}}
        @settings={{hash
          onAdd=this.onOrderAdded
          onRemove=this.onOrderRemoved
          message=(hash noResults='Aucun résultat trouvé.')
          fullTextSearch=true
        }}
        data-test-orders-dropdown
      >
        <:menu as |Item|>
          {{#each-in
            this.orderedPrescriptibleOrders
            as |group bioExamOrdersByLoincChapter|
          }}
            {{#each-in
              bioExamOrdersByLoincChapter
              as |loincChapter bioExamOrders|
            }}
              <div class='semeia-dropdown ui dividing header'>{{concat
                  loincChapter
                  ' ('
                  (t (concat 'bio-exam-order.specimen.' group))
                  ')'
                }}</div>
              {{#each bioExamOrders as |bioExamOrder|}}
                <Item @value={{bioExamOrder.loinc}}>
                  {{! @glint-expect-error }}
                  {{bioExamOrder.longLabel}}
                </Item>
              {{/each}}
            {{/each-in}}
          {{/each-in}}
        </:menu>
      </FuiDropdown>
    </div>

    <div class='mt16 mb16'>
      <div {{fuiAccordion}} class='ui accordion'>
        <div class='{{if @isActive "active"}} title'>
          <span class='ui primary text'>
            Ajouter des examens librement
            <div class='float-right'>
              <i
                data-test-expand-accordion='add-custom-orders'
                class='dropdown icon'
              ></i>
            </div>
          </span>
        </div>

        <div class='{{if @isActive "active"}} content mb16'>
          <div class='field'>
            <LabeledTextArea
              @changeset={{@changeset}}
              @labelName=''
              @propertyName='customOrders'
              @rows={{4}}
            />
          </div>
        </div>
      </div>

      <div {{fuiAccordion}} class='ui accordion'>
        <div class='{{if @isActive "active"}} title'>
          <span class='ui primary text'>
            Ajouter un commentaire
            <div class='float-right'>
              <i
                data-test-expand-accordion='add-ald-comments'
                class='dropdown icon'
              ></i>
            </div>
          </span>
        </div>

        <div class='{{if @isActive "active"}} content mb16'>
          <div class='fields'>
            <div class='fifteen wide field'>
              <LabeledTextArea
                @changeset={{@changeset}}
                @labelName=''
                @propertyName='aldRelatedComments'
              />
            </div>
            <div class='field'>
              <CommentSuggestions
                @updateComment={{this.updateAldRelatedComments}}
              />
            </div>
          </div>
        </div>
      </div>

      <div {{fuiAccordion}} class='ui accordion'>
        <div class='{{if @isActive "active"}} title'>
          <span class='ui primary text'>
            Ajouter un commentaire sans rapport avec l'affection de longue durée
            <div class='float-right'>
              <i
                data-test-expand-accordion='add-non-ald-comments'
                class='dropdown icon'
              ></i>
            </div>
          </span>
        </div>

        <div class='{{if @isActive "active"}} content mb16'>
          <div class='fields'>
            <div class='fifteen wide field'>
              <LabeledTextArea
                @labelName=''
                @propertyName='comments'
                @changeset={{@changeset}}
              />
            </div>
            <div class='field'>
              <CommentSuggestions
                @updateComment={{this.updateNonAldRelatedComments}}
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class='field'>
      <label>Fréquence</label>
      <FuiDropdown
        class='ui selection dropdown'
        @selected={{this.selectedFrequency}}
        @settings={{hash onChange=this.onFrequencyChanged}}
        data-test-dropdown='frequency'
      >
        <:menu as |Item|>
          {{#each this.frequencyOptions as |dropdownPossibility|}}
            <Item @value={{dropdownPossibility}}>
              {{capitalize
                (tWithDefault
                  (concat 'frequency.' dropdownPossibility) 'Aucune fréquence'
                )
              }}
            </Item>
          {{/each}}
        </:menu>
      </FuiDropdown>
    </div>

    <div class='field'>
      <Dropdown
        @changeset={{@changeset}}
        @labelName='Durée de validité'
        @propertyName='validityPeriod'
        @isMandatory={{true}}
        as |dropdown|
      >
        {{#each this.validityOptions as |validityOption index|}}
          {{#if (eq index 6)}}
            <dropdown.Item @key={{12}} @value={{validityOption}} />
          {{else}}
            <dropdown.Item @key={{add index 1}} @value={{validityOption}} />
          {{/if}}
        {{/each}}
      </Dropdown>
    </div>

    <div class='field'>
      <Checkbox
        @changeset={{@changeset}}
        @labelName='Je souhaite être alerté lorsque cette ordonnance arrivera à expiration'
        @payloadKey='createExpirationAlert'
      />
    </div>
  </template>
}
